import type { AdminPermission } from "@/lib/permissions/permissions";
import type { MembershipState } from "@/lib/constants/membership-states";
import type { AuditAction } from "@/lib/audit/audit-action";
import type { DangerSeverity } from "@/lib/audit/dangerous-action";
import type { AdminRestoreTargetState } from "@/lib/api/admin-contract";
import type {
  MembershipQueueTab,
  MembershipQueueTabId,
  MembershipPlaceholderActionKey,
  MembershipPlaceholderActionDescriptor,
} from "./types";

/**
 * Lifecycle queue tabs surfaced on the Membership Ops page. The "all" tab is
 * implicit (states === null) and shows every membership we know about. The
 * other tabs are the 8 lifecycle queues that humans triage day-to-day.
 */
export const MEMBERSHIP_QUEUE_TABS: readonly MembershipQueueTab[] = [
  { id: "all", label: "All", states: null },
  {
    id: "trial_card_failed",
    label: "Conversion failed",
    states: ["trial_card_failed"],
    tone: "crit",
  },
  {
    id: "trial_ending_soon",
    label: "Trial ending soon",
    states: ["trial_ending_soon"],
    tone: "warn",
  },
  {
    id: "trial_charged_off",
    label: "Charged off",
    states: ["trial_charged_off"],
    tone: "crit",
  },
  { id: "past_due", label: "Past due", states: ["past_due"], tone: "crit" },
  {
    id: "renewing_soon",
    label: "Renewing soon",
    states: ["renewing_soon"],
    tone: "info",
  },
  { id: "paused", label: "Paused", states: ["paused"] },
  { id: "cancelled", label: "Cancelled", states: ["cancelled"] },
  { id: "expired", label: "Expired", states: ["expired"] },
] as const;

export const TRIAL_MEMBERSHIP_VIEW_IDS = [
  "trial_card_failed",
  "trial_ending_soon",
  "trial_charged_off",
] as const satisfies readonly MembershipQueueTabId[];

export const DEFAULT_MEMBERSHIP_QUEUE_TAB: MembershipQueueTabId = "all";

import type { MembershipOpsFilterState } from "./types";

export const MEMBERSHIP_TABLE_PAGE_SIZE = 20;

/**
 * Tabs in the detail drawer, one set per panel. Overview first because it is
 * what an operator reads to decide; Actions last because every button on it is
 * audit-logged and some are hard to reverse.
 */
export const MEMBERSHIP_DETAIL_TABS = [
  { value: "overview", label: "Overview" },
  { value: "actions", label: "Actions" },
] as const;

export const TRIAL_DETAIL_TABS = [
  { value: "overview", label: "Overview" },
  { value: "lifecycle", label: "Lifecycle" },
  { value: "actions", label: "Actions" },
] as const;

export const INITIAL_MEMBERSHIP_FILTERS: MembershipOpsFilterState = {
  query: "",
  path: "all",
  futureBilling: "all",
  page: 1,
};

export const MEMBERSHIP_PATH_FILTER_OPTIONS = [
  { value: "all", label: "All paths" },
  { value: "trial", label: "Trial path" },
  { value: "paid_trial", label: "Paid trial" },
  { value: "direct", label: "Direct" },
  { value: "comp", label: "Comp" },
] as const;

export const MEMBERSHIP_FUTURE_BILLING_FILTER_OPTIONS = [
  { value: "all", label: "All billing" },
  { value: "annual", label: "annual" },
  { value: "monthly", label: "monthly" },
  { value: "none", label: "none" },
] as const;

/**
 * States that, when present on a row, should surface the "reclaim deadline"
 * column populated rather than dashed-out. Used purely for display affordance.
 */
export const RECLAIM_DEADLINE_STATES: readonly MembershipState[] = [
  "trial_charged_off",
  "trial_card_failed",
  "trial_ending_soon",
] as const;

const trialExtension: MembershipPlaceholderActionDescriptor = {
  key: "trial_extension",
  action: "membership.trial_extend" satisfies AuditAction,
  permission: "membership:trial_extend" satisfies AdminPermission,
  severity: "warn" satisfies DangerSeverity,
  triggerLabel: "Extend trial",
  title: "Extend trial window",
  description:
    "Adds 1–14 days to the trial before charge-off. This takes effect immediately and is audit-logged. A trial can only be extended once.",
  acknowledgement:
    "I have verified user consent for an extended trial and a support ticket is open.",
};

const manualStateOverride: MembershipPlaceholderActionDescriptor = {
  key: "manual_state_override",
  action: "membership.override_state" satisfies AuditAction,
  permission: "membership:override" satisfies AdminPermission,
  severity: "crit" satisfies DangerSeverity,
  triggerLabel: "Override state",
  title: "Force membership state",
  description:
    "Forcibly moves the membership into a chosen lifecycle state. States a dedicated permission owns (suspended, permanently closed) and states with an external cascade are not offered.",
  acknowledgement:
    "I understand this bypasses normal lifecycle transitions and will be reviewed.",
};

const commitmentOverride: MembershipPlaceholderActionDescriptor = {
  key: "commitment_override",
  action: "membership.override_commitment" satisfies AuditAction,
  permission: "membership:commitment_override" satisfies AdminPermission,
  severity: "crit" satisfies DangerSeverity,
  triggerLabel: "Override commitment",
  title: "Waive commitment",
  // W66-4: this dialog has been wired to the backend since 902144c; it said
  // "Placeholder UI only — no mutation is dispatched" until 2026-09-18.
  description:
    "Ends this member's 12-month commitment now: the commitment end date becomes today. This is real and audit-logged, and it cannot be undone here.",
  acknowledgement:
    "I have a written approval from the duty manager for this waiver.",
};

const trialConversion: MembershipPlaceholderActionDescriptor = {
  key: "trial_conversion",
  action: "membership.force_convert" satisfies AuditAction,
  permission: "membership:trial_convert" satisfies AdminPermission,
  severity: "crit" satisfies DangerSeverity,
  triggerLabel: "Force convert trial",
  title: "Force-convert trial to active",
  description:
    "Skips the trial period and converts to an active membership. Placeholder UI only — the backend mutation is not wired.",
  acknowledgement:
    "I have explicit user consent to convert this trial early.",
};

export const MEMBERSHIP_PLACEHOLDER_ACTIONS: Record<
  MembershipPlaceholderActionKey,
  MembershipPlaceholderActionDescriptor
> = {
  trial_extension: trialExtension,
  manual_state_override: manualStateOverride,
  commitment_override: commitmentOverride,
  trial_conversion: trialConversion,
};

/**
 * WF-66 — the lifecycle states a manual override may target.
 *
 * MUST stay in step with OVERRIDE_ALLOWED_TARGET_STATES in
 * gateway/app/services/admin_membership_state_override_service.py, which is the
 * authority: the backend re-validates every submission against its own copy, so
 * a drift here shows up as a 422, never as an unguarded write.
 *
 * Deliberately absent, and why:
 *   suspended, permanently_closed  — a dedicated permission owns them; offering
 *                                    them would make membership:override a back
 *                                    door around membership:suspend
 *   trial_charged_off, returned,
 *   trial_returned                 — real external cascades (serial blacklist,
 *                                    refund, sign-in disable)
 *   trial_returning,
 *   trial_card_failed              — own an in-flight reclaim/dunning clock
 *   pending_shipment,
 *   pending_delivery               — would re-open fulfillment
 */
export const OVERRIDE_TARGET_STATES = [
  "trial_active",
  "trial_ending_soon",
  "active",
  "renewing_soon",
  "past_due",
  "paused",
  "cancelled",
  "expired",
] as const;

export type OverrideTargetState = (typeof OVERRIDE_TARGET_STATES)[number];

/**
 * W66-2 — states a manual override may not LEAVE. MUST stay in step with
 * OVERRIDE_REFUSED_SOURCE_STATES in the gateway's
 * admin_membership_state_override_service.py (the backend refuses them with
 * 409 invalid_source_state either way). Each value is the copy shown.
 */
export const OVERRIDE_REFUSED_SOURCE_STATES: Record<string, string> = {
  suspended: "A suspended membership can't be changed here. Use Restore membership instead.",
  permanently_closed: "This account was closed by a dispute and can't be reopened.",
  trial_charged_off: "This trial was charged off and its serials blacklisted, so it can't be reopened.",
  returned: "This membership was returned and refunded, so it can't be reopened.",
  trial_returned: "This trial was returned, so it can't be reopened.",
};

/**
 * W66-1 — the states the Restore dialog offers. A trial member goes back to
 * trial_active; a paying member to active, past_due or paused. The gateway
 * refuses the wrong kind (409 incoherent_target). trial_returning is left to the
 * lost-report flow, which opens the hardware return along with it.
 */
export const RESTORE_TARGET_STATES = [
  "trial_active",
  "active",
  "past_due",
  "paused",
] as const satisfies readonly AdminRestoreTargetState[];
